<template>
  <ModalStep
    id="users-import-modal-content"
    data-test-id="admin-users-import-modal-content"
    :header="{
      title: t('admin.users.excelImportModal.title'),
    }"
  >
    <template #headerAfterElement>
      <ProgramReminder :program-id="selectedProgramIdRef" />
    </template>
    <template #bodyContent>
      <LoadingContent v-if="isImportLoadingRef" :text="t('admin.users.excelImportModal.importation.inProgress')" />

      <div v-else-if="isImportValidRef" class="file-uploading-container file-uploading-container--valid">
        <div class="file-uploading-container__information">
          <FileIcon color="primary" filled />
          <div class="deliverable-information">
            <p class="deliverable-information__name">{{ fileRef?.name }}</p>
            <p class="deliverable-information__sub-info">{{ t('admin.users.excelImportModal.file.valid') }}</p>
          </div>
        </div>

        <div class="file-uploading-container__status">
          <div class="deliverable-remove-btn" @click="removeFile">
            <CancelIcon color="danger" />
          </div>
        </div>
      </div>
      <template v-else>
        <div class="c-info-wrapper">
          <p class="c-info-label">
            <strong>{{ t('admin.users.excelImportModal.instructions.title') }}</strong>
          </p>
          <p class="c-info-text">
            {{ t('admin.users.excelImportModal.instructions.body.part1') }}
            <a
              :href="downloadExcelTemplate"
              :download="`${sentenceFileImport} ${selectedProgramRef?.name || ''}.xlsx`"
              >{{ t('admin.users.excelImportModal.instructions.body.downloadExcel').toLowerCase() }}</a
            >
            {{ t('admin.users.excelImportModal.instructions.body.part2') }}
            <a
              href="https://constel-education.notion.site/Structuration-du-fichier-Excel-pour-l-import-d-utilisateurs-df86bc966470475ca59710436752d391?pvs=4"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ t('admin.users.excelImportModal.instructions.body.linkToTheGuide').toLowerCase() }} </a
            >.
          </p>
        </div>
        <FileUpload :callback="onFileChange" :accept="`.xlsx`">
          <template #instructions>
            <span>{{ t('ui.input.file.instructions.format') }} .xlsx</span>
          </template>
        </FileUpload>
      </template>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton usage="button" type="submit" color="white" :text="t('ui.button.cancel')" @emit:click="onClose" />
        <CustomButton
          :isDisabled="!isImportValidRef"
          usage="button"
          type="submit"
          color="primary"
          :text="t('ui.button.next')"
          @emit:click="onNext"
          icon-position="right"
          data-test-id="admin-users-import-sumit-button"
        >
          <template #icon>
            <ArrowIcon color="white" />
          </template>
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { isValidPhoneNumber } from 'libphonenumber-js'
import { ref } from 'vue'
import ExcelJS from 'exceljs'
import { z } from 'zod'

import ProgramReminder from '@/features/programs/components/program-reminder/program-reminder.vue'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import FileUpload from '@/features/ui/components/file-upload/file-upload.vue'
import ArrowIcon from '@/features/ui/components/icons/arrow-icon.vue'
import CancelIcon from '@/features/ui/components/icons/cancel-icon.vue'
import FileIcon from '@/features/ui/components/icons/file-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import type { AdminUsersImportModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const toast = useToast()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef: selectedProgramRef } = useProgram(selectedProgramIdRef)

const fileRef = ref<File | null>(null)
const downloadExcelTemplate = ref<string>('')
let sentenceFileImport = t('admin.users.excelImportModal.instructions.body.sentenceFileImport')

function onFileChange(file: File) {
  fileRef.value = file
  importXLSX()
}

// remove the file
function removeFile() {
  fileRef.value = null
  isImportValidRef.value = false
}

const isImportValidRef = ref<boolean>(false)
const isImportLoadingRef = ref<boolean>(false)

function isValidUUID(value: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(value)
}

function extractPlaceholdersAndIds(input: string) {
  // Expression régulière pour trouver :
  // - `%{` suivi de n'importe quel texte non '}' jusqu'à `}%`
  // - suivi d'un `[` et de n'importe quel texte non ']' jusqu'à `]`
  const regex = /%\{([^}]+)\}%\[([^\]]+)\]/g
  const results = []
  let match

  while ((match = regex.exec(input)) !== null) {
    const placeholder = match[1]
    const inputId = match[2]
    results.push({ placeholder, inputId })
  }

  return results
}
function addPlaceholdersToData(data: Record<string, any>[], placeholders: { placeholder: string; inputId: string }[]) {
  return data.map((entry) => {
    const extendedEntry = { ...entry }
    placeholders.forEach(({ placeholder, inputId }) => {
      extendedEntry[`${placeholder}[${inputId}]`] = ''
    })
    return extendedEntry
  })
}

const generateAndDownloadExcel = async () => {
  const rawData = [
    {
      email: 'john.smith@example.com',
      firstname: 'John',
      lastname: 'Smith',
      roles: 'MANAGER, MENTOR',
      phone: '+33771104405',
      sso: 'false',
      language: 'FR',
      description: "Description de l'utilisateur",
    },
    {
      email: 'jane.doe@example.com',
      firstname: 'Jane',
      lastname: 'Doe',
      roles: 'MENTEE',
      phone: '+33612345678',
      sso: 'false',
      language: 'FR',
      description: "Description du mentoré",
    },
  ]

  // Critères de matching mentor avec préfixe [MENTOR]
  const mentorMatchingCriteria = selectedProgramRef.value?.settings.mentorMatchingCriteria || ''
  const mentorPlaceholders = extractPlaceholdersAndIds(mentorMatchingCriteria).map((p) => ({
    ...p,
    placeholder: `[MENTOR] ${p.placeholder}`,
  }))

  // Critères de matching mentee avec préfixe [MENTEE]
  const menteeMatchingCriteria = selectedProgramRef.value?.settings.menteeMatchingCriteria || ''
  const menteePlaceholders = extractPlaceholdersAndIds(menteeMatchingCriteria).map((p) => ({
    ...p,
    placeholder: `[MENTEE] ${p.placeholder}`,
  }))

  // Combiner les deux listes de placeholders (d'abord mentor, puis mentee)
  const placeholders = [...mentorPlaceholders, ...menteePlaceholders]

  const enrichedData = addPlaceholdersToData(rawData, placeholders)

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Données de Test')

  if (enrichedData.length > 0) {
    // Add headers
    const headers = Object.keys(enrichedData[0])
    worksheet.addRow(headers)
    
    // Add data rows
    enrichedData.forEach(row => {
      worksheet.addRow(Object.values(row))
    })
  }

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const url = URL.createObjectURL(blob)
  downloadExcelTemplate.value = url
}

generateAndDownloadExcel()

async function importXLSX() {
  if (!fileRef.value) return
  const file = fileRef.value
  const reader = new FileReader()

  reader.onload = async (e) => {
    const errors: string[] = []
    isImportLoadingRef.value = true
    
    try {
      const data = new Uint8Array(e.target?.result as ArrayBuffer)
      const workbook = new ExcelJS.Workbook()
      await workbook.xlsx.load(data.buffer)

      const worksheet = workbook.getWorksheet(1) // First worksheet
      if (!worksheet) throw new Error('No worksheet found')

      const jsonData: Record<string, any>[] = []
      let headers: string[] = []
      
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) {
          // First row contains headers
          headers = row.values as string[]
          headers.shift() // Remove first empty element from ExcelJS
        } else {
          // Data rows
          const rowData: Record<string, any> = {}
          const values = row.values as any[]
          values.shift() // Remove first empty element from ExcelJS
          
          headers.forEach((header, index) => {
            rowData[header] = values[index] ?? null
          })
          jsonData.push(rowData)
        }
      })

    const allHeaders = Object.keys(jsonData[0] || [])

    // Liste des utilisateurs transformés
    const usersToImport = (jsonData as Record<string, any>[]).map((row) => {
      const user: { fields: { key: string; value: any }[] } = { fields: [] }

      allHeaders.forEach((header) => {
        const uuidMatch = header.match(/\[([a-f0-9-]+)\]$/)
        if (uuidMatch) {
          let fieldName = header.replace(/\[([a-f0-9-]+)\]$/, '').trim()
          const uuid = uuidMatch[1]

          // Détecter le préfixe [MENTOR] ou [MENTEE]
          let criteriaType: 'mentor' | 'mentee' | null = null
          if (fieldName.startsWith('[MENTOR] ')) {
            criteriaType = 'mentor'
          } else if (fieldName.startsWith('[MENTEE] ')) {
            criteriaType = 'mentee'
          }

          if (isValidUUID(uuid)) {
            // Garder le préfixe dans le nom de la colonne pour le tableau de confirmation
            user.fields.push({ key: fieldName, value: { uuid, value: row[header] ?? null, criteriaType } })
          } else {
            errors.push(t('admin.users.excelImportModal.error.invalidUUID', { field: fieldName, uuid }))
            user.fields.push({ key: header, value: row[header] ?? null }) // Ajout en champ fixe
          }
        } else {
          user.fields.push({ key: header, value: row[header] ?? null })
        }
      })

      return user
    })

    for (let user of usersToImport) {
      // Assigner `programId` si absent
      if (!user.fields.some((f) => f.key === 'programId')) {
        user.fields.push({ key: 'programId', value: selectedProgramIdRef.value! })
      }

      // Validation des emails
      const emailField = user.fields.find((f) => f.key === 'email')
      if (emailField?.value) {
        emailField.value = emailField.value.toString().trim().toLowerCase()

        try {
          z.string().email().parse(emailField.value)
        } catch (error: any) {
          if (!user.fields.find((f) => f.key === 'firstname')?.value) {
            errors.push(t('admin.users.excelImportModal.error.invalidEmailForOne'))
          } else {
            errors.push(
              t('admin.users.excelImportModal.error.invalidEmailFor', {
                firstName: user.fields.find((f) => f.key === 'firstname')?.value,
                lastName: user.fields.find((f) => f.key === 'lastname')?.value,
              }),
            )
          }
        }
      } else {
        if (!user.fields.find((f) => f.key === 'firstname')?.value) {
          errors.push(t('admin.users.excelImportModal.error.missingEmailForOne'))
        } else {
          errors.push(
            t('admin.users.excelImportModal.error.missingEmailFor', {
              firstName: user.fields.find((f) => f.key === 'firstname')?.value,
              lastName: user.fields.find((f) => f.key === 'lastname')?.value,
            }),
          )
        }
      }

      // Validation du role
      const roleField = user.fields.find((f) => f.key === 'roles')
      if (roleField?.value) {
        // Support multiple roles separated by commas
        const allowedRoles = ['ADMIN', 'MANAGER', 'MENTOR', 'MENTEE']
        const roles = roleField.value
          .toString()
          .split(',')
          .map((r: string) => r.trim())
          .filter((r: string) => r.length > 0)

        const invalidRoles = roles.filter((role: string) => !allowedRoles.includes(role))
        if (invalidRoles.length > 0) {
          if (!user.fields.find((f) => f.key === 'firstname')?.value) {
            errors.push(t('admin.users.excelImportModal.error.invalidRoleForOne'))
          } else {
            errors.push(
              t('admin.users.excelImportModal.error.invalidRoleFor', {
                firstName: user.fields.find((f) => f.key === 'firstname')?.value,
                lastName: user.fields.find((f) => f.key === 'lastname')?.value,
              }),
            )
          }
        }
        // Optionally, you can normalize the value to an array or a string
        roleField.value = roles
      } else {
        if (!user.fields.find((f) => f.key === 'firstname')?.value) {
          errors.push(t('admin.users.excelImportModal.error.missingRoleForOne'))
        } else {
          errors.push(
            t('admin.users.excelImportModal.error.missingRoleFor', {
              firstName: user.fields.find((f) => f.key === 'firstname')?.value,
              lastName: user.fields.find((f) => f.key === 'lastname')?.value,
            }),
          )
        }
      }

      // Validation du téléphone
      const phoneField = user.fields.find((f) => f.key === 'phone')
      if (phoneField?.value) {
        phoneField.value = phoneField.value.toString().replace(/[.\s]/g, '')
        if (phoneField.value.startsWith('0')) {
          phoneField.value = '+33' + phoneField.value.substring(1)
        }

        if (!isValidPhoneNumber(phoneField.value)) {
          errors.push(
            t('admin.users.excelImportModal.error.invalidPhoneFor', {
              firstName: user.fields.find((f) => f.key === 'firstname')?.value,
              lastName: user.fields.find((f) => f.key === 'lastname')?.value,
            }),
          )
        }
      }

      // Normalisation du SSO
      const ssoField = user.fields.find((f) => f.key === 'sso')
      if (ssoField?.value) {
        ssoField.value = Boolean(ssoField.value.toString().trim().toLowerCase() === 'true')
      } else {
        user.fields.push({ key: 'sso', value: false })
      }
    }

    // Affichage des erreurs et arrêt si nécessaire
    if (errors.length > 0) {
      errors.forEach((error) => toast?.error(error))
      isImportValidRef.value = false
      isImportLoadingRef.value = false
      return
    }

    // Import validé
    isImportValidRef.value = true
    isImportLoadingRef.value = false
    toast?.success(t('admin.users.excelImportModal.validStructure'))

    modalsStore.updateModalAttachedData<AdminUsersImportModalType>('admin-users-import', {
      users: usersToImport,
    })
    
    } catch (error) {
      console.error('Error reading Excel file:', error)
      toast?.error(t('admin.users.excelImportModal.error.fileRead'))
      isImportValidRef.value = false
      isImportLoadingRef.value = false
    }
  }

  reader.readAsArrayBuffer(file)
}

function onNext() {
  modalsStore.setModalStep<AdminUsersImportModalType>('admin-users-import', 'users-import-confirmation')
}

function onClose() {
  modalsStore.toggleModal('admin-users-import', false)
}
</script>

<style lang="scss">
@import './users-import-modal-content.scss';
</style>
